import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './Settings.css';
import PageTitle from '../shared/PageTitle';
import { useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://nivaas.app';

const Settings = () => {
  const fileInputRef = useRef(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testingKey, setTestingKey] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [cleanupResults, setCleanupResults] = useState({});
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [qrCodePreview, setQrCodePreview] = useState(null);
  const [settings, setSettings] = useState({
    platform_name: 'Society Management App',
    platform_email: 'support@example.com',
    platform_description: 'Manage your society or residential complex efficiently',
    allow_tenant_registration: true,
    default_plan: 'Free',
    // Payment gateway settings - support multiple gateways
    stripe_enabled: false,
    razorpay_enabled: false,
    offline_payment_enabled: true,
    // Legacy support
    payment_gateway_enabled: true,
    // Stripe settings
    stripe_public_key: '',
    stripe_secret_key: '',
    // Razorpay settings
    razorpay_key_id: '',
    razorpay_key_secret: '',
    // Offline payment settings
    offline_payment_qr_code: '',
    offline_payment_bank_name: '',
    offline_payment_account_number: '',
    offline_payment_ifsc_code: '',
    offline_payment_account_holder: '',
    offline_payment_upi_id: '',
    offline_payment_instructions: 'Please transfer the amount to the above account details and share the transaction screenshot for verification.',
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_pass: '',
    smtp_from_email: '',
    logo_url: '',
    auto_cleanup_visitors: false,
    auto_cleanup_financials: false,
    auto_cleanup_activity: false,
    auto_cleanup_complaints: false
  });

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/saas/subscriptions/plans`);
        const fetchedPlans = response.data;
        console.log('fetchedPlans', fetchedPlans);
        setPlans(fetchedPlans);
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Fetch settings on initial load
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        await fetchSettingsFromServer();
        setLoading(false);
      } catch (error) {
        console.error('Error in initial settings fetch:', error);
        toast.error('Failed to load platform settings');
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prevSettings => ({
      ...prevSettings,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle QR code file upload
  const handleQrCodeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQrCodeFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setQrCodePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload QR code
  const uploadQrCode = async () => {
    if (!qrCodeFile) return null;

    try {
      console.log('Uploading QR code file:', qrCodeFile.name, qrCodeFile.size, 'bytes');

      // Validate file size (5MB limit)
      if (qrCodeFile.size > 5 * 1024 * 1024) {
        toast.error('QR code file size exceeds 5MB limit');
        return null;
      }

      // Validate file type
      if (!qrCodeFile.type.startsWith('image/')) {
        toast.error('Please select a valid image file for QR code');
        return null;
      }

      const formData = new FormData();
      formData.append('qr_code', qrCodeFile);

      const response = await axios.post('/api/saas/settings/qr-code', formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('QR code upload successful:', response.data);
      toast.success('QR code uploaded successfully');
      return response.data.qr_code_url;
    } catch (error) {
      console.error('Error uploading QR code:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload QR code';
      toast.error(errorMessage);
      return null;
    }
  };

  // Upload logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);

      console.log('Uploading logo file:', file.name, file.size, 'bytes');

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Logo file size exceeds 5MB limit');
        setLoading(false);
        fileInputRef.current.value = '';
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPG, JPEG, and PNG files are allowed');
        setLoading(false);
        fileInputRef.current.value = '';
        return;
      }

      const formData = new FormData();
      formData.append('logo', file);

      const response = await axios.post('/api/saas/settings/logo', formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSettings(prev => ({
        ...prev,
        logo_url: response.data.logo_url
      }));

      console.log('Logo upload successful:', response.data);
      toast.success('Logo uploaded successfully');
      setLoading(false);
    } catch (error) {
      console.error('Error uploading logo:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload logo';
      toast.error(errorMessage);
      setLoading(false);
      fileInputRef.current.value = '';
    }
  };

  // Save general settings
  const saveGeneralSettings = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let qrCodeUrl = settings.offline_payment_qr_code;
      if (qrCodeFile) {
        const uploadedUrl = await uploadQrCode();
        if (uploadedUrl) qrCodeUrl = uploadedUrl;
      }

      const settingsToSave = { ...settings };
      if (qrCodeUrl) settingsToSave.offline_payment_qr_code = qrCodeUrl;

      delete settingsToSave.stripe_secret_key;

      await axios.post('/api/saas/settings', settingsToSave, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('General settings saved successfully');
      setTimeout(fetchSettingsFromServer, 1000);
    } catch (error) {
      console.error('Error saving platform settings:', error);
      toast.error(`Failed to save platform settings: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveEmailSettings = async () => {
    try {
      setLoading(true);

      const emailSettings = {
        smtp_host: settings.smtp_host,
        smtp_port: settings.smtp_port,
        smtp_user: settings.smtp_user,
        smtp_pass: settings.smtp_pass,
        smtp_from_email: settings.smtp_from_email
      };

      await axios.post('/api/saas/settings', emailSettings, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Email settings saved successfully.');
      setTimeout(fetchSettingsFromServer, 1000);
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error(`Failed to save email settings: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Separate function to fetch settings for reuse
  const fetchSettingsFromServer = async () => {
    try {
      console.log('Fetching latest settings from server');
      const response = await axios.get('/api/saas/settings', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data?.data) {
        console.log('Settings fetched:', response.data.data);
        const data = response.data.data;
        // Normalize all boolean settings
        const normalized = {
          ...data,
          // Payment gateway settings
          stripe_enabled: data.stripe_enabled === 'true',
          razorpay_enabled: data.razorpay_enabled === 'true',
          offline_payment_enabled: data.offline_payment_enabled === 'true' || data.offline_payment_enabled === true,
          payment_gateway_enabled: data.payment_gateway_enabled === 'true',
          // Cleanup settings
          auto_cleanup_visitors: data.auto_cleanup_visitors === 'true',
          auto_cleanup_financials: data.auto_cleanup_financials === 'true',
          auto_cleanup_activity: data.auto_cleanup_activity === 'true',
          auto_cleanup_complaints: data.auto_cleanup_complaints === 'true'
        };
        // Merge saved settings with default values
        setSettings(prevSettings => ({
          ...prevSettings,
          ...normalized
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Test Stripe keys
  const testStripeKeys = async () => {
    try {
      setTestingKey(true);
      setTestResult(null);

      const testData = {
        stripe_secret_key: settings.stripe_secret_key
      };

      const response = await axios.post('/api/saas/settings/stripe/test', testData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setTestResult({
        success: true,
        message: response.data.message || 'Stripe keys are valid'
      });

      toast.success('Stripe keys are valid');
      setTestingKey(false);
    } catch (error) {
      console.error('Error testing Stripe keys:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.message || 'Failed to test Stripe keys'
      });
      toast.error(`Stripe key test failed: ${error.response?.data?.message || error.message}`);
      setTestingKey(false);
    }
  };

  // Cleanup functions
  const runCleanup = async (type) => {
    try {
      setLoading(true);

      const response = await axios.post('/api/saas/cleanup/manual', {
        category: type
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setCleanupResults(prev => ({
        ...prev,
        [type]: response.data
      }));

      toast.success(`${type} cleanup completed successfully`);
      setLoading(false);
    } catch (error) {
      console.error(`Error running ${type} cleanup:`, error);
      toast.error(`Failed to run ${type} cleanup: ${error.response?.data?.message || error.message}`);
      setLoading(false);
    }
  };

  // Save payment settings separately
  const savePaymentSettings = async () => {
    try {
      setLoading(true);

      // Upload QR code if a new one was selected
      let qrCodeUrl = settings.offline_payment_qr_code;
      if (qrCodeFile) {
        const uploadedUrl = await uploadQrCode();
        if (uploadedUrl) {
          qrCodeUrl = uploadedUrl;
        }
      }

      const paymentSettings = {
        // Payment gateway toggles
        stripe_enabled: settings.stripe_enabled,
        razorpay_enabled: settings.razorpay_enabled,
        offline_payment_enabled: settings.offline_payment_enabled,
        // Legacy support
        payment_gateway_enabled: settings.stripe_enabled || settings.razorpay_enabled,
        // Stripe settings
        stripe_public_key: settings.stripe_public_key,
        // Razorpay settings
        razorpay_key_id: settings.razorpay_key_id,
        // Offline payment settings
        offline_payment_qr_code: qrCodeUrl,
        offline_payment_bank_name: settings.offline_payment_bank_name,
        offline_payment_account_number: settings.offline_payment_account_number,
        offline_payment_ifsc_code: settings.offline_payment_ifsc_code,
        offline_payment_account_holder: settings.offline_payment_account_holder,
        offline_payment_upi_id: settings.offline_payment_upi_id,
        offline_payment_instructions: settings.offline_payment_instructions
      };

      // Convert boolean values to strings for storage
      Object.keys(paymentSettings).forEach(key => {
        if (typeof paymentSettings[key] === 'boolean') {
          paymentSettings[key] = paymentSettings[key].toString();
        }
      });

      await axios.post('/api/saas/settings', paymentSettings, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Payment settings saved successfully');

      // Refresh the settings to make sure we have the latest data
      setTimeout(() => {
        fetchSettingsFromServer();
      }, 1000);

      setLoading(false);
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toast.error(`Failed to save payment settings: ${error.response?.data?.message || error.message}`);
      setLoading(false);
    }
  };

  // Save cleanup settings separately
  const saveCleanupSettings = async () => {
    try {
      setLoading(true);

      const cleanupSettings = {
        auto_cleanup_visitors: settings.auto_cleanup_visitors,
        auto_cleanup_financials: settings.auto_cleanup_financials,
        auto_cleanup_activity: settings.auto_cleanup_activity,
        auto_cleanup_complaints: settings.auto_cleanup_complaints
      };

      // Convert boolean values to strings for storage
      Object.keys(cleanupSettings).forEach(key => {
        if (typeof cleanupSettings[key] === 'boolean') {
          cleanupSettings[key] = cleanupSettings[key].toString();
        }
      });

      await axios.post('/api/saas/settings', cleanupSettings, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Cleanup settings saved successfully');
      setLoading(false);
    } catch (error) {
      console.error('Error saving cleanup settings:', error);
      toast.error(`Failed to save cleanup settings: ${error.response?.data?.message || error.message}`);
      setLoading(false);
    }
  };
  console.log('checkkkkkkkkk', process.env.REACT_APP_API_URL);
  return (
    <div className="container-fluid mt-4">
      <PageTitle
        icon="fa-cogs"
        title="Platform Settings"
        className="mb-4"
      />
      <p className="mb-4 text-muted">Configure global settings for your SaaS platform</p>

      <div className="card shadow mb-4 settings-section">
        <div className="py-3 superadmin-section">
          <h6 className="m-0 font-weight-bold">General Settings</h6>
        </div>
        <div className="card-body">
          <form onSubmit={saveGeneralSettings}>
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Platform Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="platform_name"
                  value={settings.platform_name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Support Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="platform_email"
                  value={settings.platform_email}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Platform Description</label>
              <textarea
                className="form-control"
                name="platform_description"
                value={settings.platform_description}
                onChange={handleInputChange}
                rows="3"
              ></textarea>
            </div>

            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                name="allow_tenant_registration"
                checked={settings.allow_tenant_registration == "true" || settings.allow_tenant_registration == true}
                value={settings.allow_tenant_registration}
                onChange={handleInputChange}
                id="allow_registration"
              />
              <label className="form-check-label" htmlFor="allow_registration">
                Allow public organization registration
              </label>
            </div>

            <div className="mb-3">
              <label className="form-label">Default Plan for New Organizations</label>
              <select
                className="form-control"
                name="default_plan"
                value={settings.default_plan}
                onChange={handleInputChange}
              >
                {plans.map(plan => (
                  <option key={plan.id} value={plan.name}>{plan.name}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Platform Logo</label>
              <div className="input-group">
                <input type="file" className="form-control" onChange={handleLogoUpload} ref={fileInputRef} accept="image/*" />
              </div>
              <small className="text-muted">
                Upload your platform logo (Max 5MB, PNG/JPG/JPEG supported)
              </small>
              {settings.logo_url && (
                <div className="mt-2">
                  <img src={`${process.env.REACT_APP_API_URL}${settings.logo_url}`} alt="Platform Logo" style={{ maxHeight: '100px' }} />
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Saving...
                  </>
                ) : (
                  'Save General Settings'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Email Settings */}
      <div className="card shadow mb-4 settings-section">
        <div className="py-3 superadmin-section">
          <h6 className="m-0 font-weight-bold">Email Settings (SMTP)</h6>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">SMTP Host</label>
              <input
                type="text"
                className="form-control"
                name="smtp_host"
                value={settings.smtp_host}
                onChange={handleInputChange}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">SMTP Port</label>
              <input
                type="number"
                className="form-control"
                name="smtp_port"
                value={settings.smtp_port}
                onChange={handleInputChange}
                placeholder="587"
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">SMTP Username</label>
              <input
                type="text"
                className="form-control"
                name="smtp_user"
                value={settings.smtp_user}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">SMTP Password</label>
              <input
                type="password"
                className="form-control"
                name="smtp_pass"
                value={settings.smtp_pass}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">From Email</label>
            <input
              type="email"
              className="form-control"
              name="smtp_from_email"
              value={settings.smtp_from_email}
              onChange={handleInputChange}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-success"
              onClick={saveEmailSettings}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Save Email Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="card shadow mb-4 settings-section">
        <div className="py-3 superadmin-section">
          <h6 className="m-0 font-weight-bold">Payment Integration</h6>
        </div>
        <div className="card-body">
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="text-primary mb-3">
                <i className="fas fa-credit-card me-2"></i>
                Payment Gateway Options
              </h6>
              <p className="text-muted mb-3">Enable multiple payment methods to give your customers more options. Each gateway can be enabled or disabled independently.</p>

              {/* Payment Methods Summary */}
              {(settings.stripe_enabled || settings.razorpay_enabled || settings.offline_payment_enabled) && (
                <div className="alert alert-info">
                  <strong>Currently Enabled: </strong>
                  {[
                    settings.stripe_enabled && 'Stripe',
                    settings.razorpay_enabled && 'Razorpay',
                    settings.offline_payment_enabled && 'Offline Payments'
                  ].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Payment Gateway Toggles */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card border-primary">
                <div className="card-body text-center">
                  <div className="form-check form-switch d-flex justify-content-center mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="stripe_enabled"
                      id="stripe_enabled"
                      checked={settings.stripe_enabled}
                      onChange={handleInputChange}
                    />
                  </div>
                  <h6 className="card-title">
                    <i className="fab fa-stripe me-2"></i>
                    Stripe
                  </h6>
                  <p className="card-text text-muted small">
                    Credit/Debit Cards, Digital Wallets
                  </p>
                  <span className={`badge ${settings.stripe_enabled ? 'bg-success' : 'bg-secondary'}`}>
                    {settings.stripe_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-info">
                <div className="card-body text-center">
                  <div className="form-check form-switch d-flex justify-content-center mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="razorpay_enabled"
                      id="razorpay_enabled"
                      checked={settings.razorpay_enabled}
                      onChange={handleInputChange}
                    />
                  </div>
                  <h6 className="card-title">
                    <i className="fas fa-inr me-2"></i>
                    Razorpay
                  </h6>
                  <p className="card-text text-muted small">
                    UPI, NetBanking, Cards, Wallets
                  </p>
                  <span className={`badge ${settings.razorpay_enabled ? 'bg-success' : 'bg-secondary'}`}>
                    {settings.razorpay_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-warning">
                <div className="card-body text-center">
                  <div className="form-check form-switch d-flex justify-content-center mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="offline_payment_enabled"
                      id="offline_payment_enabled"
                      checked={settings.offline_payment_enabled}
                      onChange={handleInputChange}
                    />
                  </div>
                  <h6 className="card-title">
                    <i className="fas fa-money-bill-wave me-2"></i>
                    Offline Payments
                  </h6>
                  <p className="card-text text-muted small">
                    Cash, Bank Transfer, QR Code
                  </p>
                  <span className={`badge ${settings.offline_payment_enabled ? 'bg-success' : 'bg-secondary'}`}>
                    {settings.offline_payment_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stripe Settings */}
          {settings.stripe_enabled && (
            <div className="payment-gateway-section mb-4">
              <h6 className="text-primary mb-3">
                <i className="fab fa-stripe me-2"></i>
                Stripe Configuration
              </h6>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Stripe Publishable Key</label>
                  <input
                    type="text"
                    className="form-control"
                    name="stripe_public_key"
                    value={settings.stripe_public_key}
                    onChange={handleInputChange}
                    placeholder="pk_test_..."
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Stripe Secret Key</label>
                  <input
                    type="password"
                    className="form-control"
                    name="stripe_secret_key"
                    value={settings.stripe_secret_key}
                    onChange={handleInputChange}
                    placeholder="sk_test_..."
                  />
                </div>
              </div>

              <div className="d-flex justify-content-start">
                <button
                  type="button"
                  className="btn btn-outline-info"
                  onClick={testStripeKeys}
                  disabled={testingKey || !settings.stripe_secret_key}
                >
                  {testingKey ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Testing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check-circle me-2"></i>
                      Test Stripe Keys
                    </>
                  )}
                </button>
              </div>

              {testResult && (
                <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'} mt-3`}>
                  <i className={`fas ${testResult.success ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                  {testResult.message}
                </div>
              )}
            </div>
          )}

          {/* Razorpay Settings */}
          {settings.razorpay_enabled && (
            <div className="payment-gateway-section mb-4">
              <h6 className="text-info mb-3">
                <i className="fas fa-inr me-2"></i>
                Razorpay Configuration
              </h6>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Razorpay Key ID</label>
                  <input
                    type="text"
                    className="form-control"
                    name="razorpay_key_id"
                    value={settings.razorpay_key_id}
                    onChange={handleInputChange}
                    placeholder="rzp_test_..."
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Razorpay Key Secret</label>
                  <input
                    type="password"
                    className="form-control"
                    name="razorpay_key_secret"
                    value={settings.razorpay_key_secret}
                    onChange={handleInputChange}
                    placeholder="Enter your Razorpay secret key"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Offline Payment Settings */}
          {settings.offline_payment_enabled && (
            <div className="offline-payment-section mb-4">
              <h6 className="text-warning mb-3">
                <i className="fas fa-money-bill-wave me-2"></i>
                Offline Payment Configuration
              </h6>

              {/* QR Code Upload */}
              <div className="mb-3">
                <label className="form-label">Payment QR Code</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleQrCodeUpload}
                />
                <small className="text-muted">
                  Upload a QR code for UPI/digital payments (Max 5MB, PNG/JPG/JPEG supported)
                </small>

                {/* QR Code Preview */}
                {(qrCodePreview || settings.offline_payment_qr_code) && (
                  <div className="mt-2">
                    <img
                      src={qrCodePreview || (settings.offline_payment_qr_code.startsWith('http')
                        ? settings.offline_payment_qr_code
                        : `${process.env.REACT_APP_API_URL || ''}${settings.offline_payment_qr_code}`)}
                      alt="Payment QR Code"
                      className="qr-code-preview"
                    />
                    {qrCodePreview && (
                      <div className="mt-1">
                        <small className="text-success">
                          <i className="fas fa-check-circle me-1"></i>
                          New QR code selected. Save payment settings to apply changes.
                        </small>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Bank Details */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Bank Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="offline_payment_bank_name"
                    value={settings.offline_payment_bank_name}
                    onChange={handleInputChange}
                    placeholder="e.g., State Bank of India"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Account Holder Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="offline_payment_account_holder"
                    value={settings.offline_payment_account_holder}
                    onChange={handleInputChange}
                    placeholder="Account holder's full name"
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Account Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="offline_payment_account_number"
                    value={settings.offline_payment_account_number}
                    onChange={handleInputChange}
                    placeholder="Bank account number"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">IFSC Code</label>
                  <input
                    type="text"
                    className="form-control"
                    name="offline_payment_ifsc_code"
                    value={settings.offline_payment_ifsc_code}
                    onChange={handleInputChange}
                    placeholder="e.g., SBIN0001234"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">UPI ID (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  name="offline_payment_upi_id"
                  value={settings.offline_payment_upi_id}
                  onChange={handleInputChange}
                  placeholder="e.g., yourname@paytm"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Payment Instructions</label>
                <textarea
                  className="form-control"
                  name="offline_payment_instructions"
                  value={settings.offline_payment_instructions}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Instructions for customers on how to make offline payments"
                ></textarea>
              </div>
            </div>
          )}

          {/* Save Payment Settings Button */}
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-success"
              onClick={savePaymentSettings}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Save Payment Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Data Cleanup Settings */}
      <div className="card shadow mb-4 settings-section">
        <div className="py-3 superadmin-section">
          <h6 className="m-0 font-weight-bold">Data Cleanup Settings</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="auto_cleanup_visitors"
                  id="auto_cleanup_visitors"
                  checked={settings.auto_cleanup_visitors}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="auto_cleanup_visitors">
                  Auto-cleanup old visitor records (90+ days)
                </label>
              </div>

              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="auto_cleanup_financials"
                  id="auto_cleanup_financials"
                  checked={settings.auto_cleanup_financials}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="auto_cleanup_financials">
                  Auto-cleanup old financial records (365+ days)
                </label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="auto_cleanup_activity"
                  id="auto_cleanup_activity"
                  checked={settings.auto_cleanup_activity}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="auto_cleanup_activity">
                  Auto-cleanup old activity logs (30+ days)
                </label>
              </div>

              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="auto_cleanup_complaints"
                  id="auto_cleanup_complaints"
                  checked={settings.auto_cleanup_complaints}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="auto_cleanup_complaints">
                  Auto-cleanup resolved complaints (180+ days)
                </label>
              </div>
            </div>
          </div>

          {/* Save Cleanup Settings Button */}
          <div className="d-flex justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-success"
              onClick={saveCleanupSettings}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Save Cleanup Settings
                </>
              )}
            </button>
          </div>

          {/* Manual Cleanup Buttons */}
          <div className="manual-cleanup-section">
            <h6 className="mb-3">Manual Cleanup</h6>
            <div className="row">
              <div className="col-md-3 mb-2">
                <button
                  className="btn cleanup-button w-100"
                  onClick={() => runCleanup('visitors')}
                  disabled={loading}
                >
                  <i className="fas fa-users me-2"></i>
                  Cleanup Visitors
                </button>
              </div>
              <div className="col-md-3 mb-2">
                <button
                  className="btn cleanup-button w-100"
                  onClick={() => runCleanup('financials')}
                  disabled={loading}
                >
                  <i className="fas fa-money-bill me-2"></i>
                  Cleanup Financials
                </button>
              </div>
              <div className="col-md-3 mb-2">
                <button
                  className="btn cleanup-button w-100"
                  onClick={() => runCleanup('activity')}
                  disabled={loading}
                >
                  <i className="fas fa-history me-2"></i>
                  Cleanup Activity
                </button>
              </div>
              <div className="col-md-3 mb-2">
                <button
                  className="btn cleanup-button w-100"
                  onClick={() => runCleanup('complaints')}
                  disabled={loading}
                >
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Cleanup Complaints
                </button>
              </div>
            </div>
          </div>

          {/* Cleanup Results */}
          {Object.keys(cleanupResults).length > 0 && (
            <div className="cleanup-results">
              <h6>Cleanup Results:</h6>
              {Object.entries(cleanupResults).map(([type, result]) => (
                <div key={type} className="cleanup-result-item">
                  <strong>{type}:</strong> {result.message || `${result.result?.recordsDeleted || 0} records and ${result.result?.filesDeleted || 0} files cleaned up`}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings; 